/** A company-reference list as a profile stores it (`agents`, `vendors`). */
export type CompanyRefs = string | { id: string }[] | null | undefined;

/**
 * Extracts the ids from an `agents` / `vendors` company-reference list.
 *
 * **This used to accept only a string, and that was the bug.** The column is
 * selected as `(supplier.data->'agents')->'value'`; `->` yields jsonb, which
 * node-postgres parses into a real array before it is ever serialised. So the
 * value arriving here is normally an **array**, `JSON.parse` threw on it, the
 * `catch` swallowed the throw, and the Agents and Vendors columns rendered empty
 * for every row. Nothing surfaced it because the failure mode was an empty cell.
 *
 * Legacy profiles that stored the blob as text still send a JSON string, so both
 * shapes are real and both are handled — which is exactly what the contract
 * declares.
 */
export function companyRefIds(raw: CompanyRefs): string[] {
  if (!raw) return [];

  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];
  return parsed
    .map(item =>
      item && typeof item === 'object' && 'id' in item
        ? String((item as { id: unknown }).id)
        : null,
    )
    .filter((id): id is string => Boolean(id));
}

/**
 * Filter options for a company-role column (Agents, Vendors): only the
 * companies that `pick` references on at least one of `companies`, labelled
 * from `labelSource`, deduped and sorted by label.
 *
 * DELTA-2541 — these filters used to offer the whole network, so filtering by
 * agent listed every supplier too. Pass the **whole** network dropdown as
 * `companies`, not the loaded table page: a page only knows the agents of its
 * own rows, and once a filter narrows the rows the list collapses to the
 * selection. An id `labelSource` cannot label is dropped — there is nothing to
 * show for it.
 */
export function referencedCompanyOptions<T>(
  companies: T[],
  pick: (company: T) => string[] | undefined,
  labelSource: { key: string; label: string }[],
): { key: string; label: string }[] {
  const labels = new Map(labelSource.map(o => [o.key, o.label]));
  const seen = new Map<string, string>();
  for (const company of companies) {
    for (const id of pick(company) ?? []) {
      if (seen.has(id)) continue;
      const label = labels.get(id);
      if (label) seen.set(id, label);
    }
  }
  return [...seen.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
